import { z } from "zod";
import {
  submissionActivityDateFields,
  submissionActivityFields,
} from "@quake/isomorphic/schemas/job-submission-activity.schema";

export const sortableSubmissionColumns = ["stage", "created_at", "updated_at", ...submissionActivityFields] as const;
const ids = z.array(z.uuid()).max(50);
const strings = z.array(z.string().min(1)).max(50);
const dateRange = z
  .object({ from: z.iso.date().optional(), to: z.iso.date().optional() })
  .refine((value) => !value.from || !value.to || value.from <= value.to);
const columnFiltersSchema = z.object({
  candidates: ids.optional(),
  jobs: ids.optional(),
  clients: ids.optional(),
  stages: strings.optional(),
  candidate_statuses: strings.optional(),
  owners: strings.optional(),
  job_owners: strings.optional(),
  candidate_employer: z.string().max(200).optional(),
  application_status: z.string().max(200).optional(),
  created_at: dateRange.optional(),
  updated_at: dateRange.optional(),
});
export type ColumnFilters = Required<Omit<z.infer<typeof columnFiltersSchema>, "created_at" | "updated_at">> & {
  candidate_text: string;
  created_at?: z.infer<typeof dateRange>;
  updated_at?: z.infer<typeof dateRange>;
};
export const emptyColumnFilters: ColumnFilters = {
  candidate_text: "",
  candidates: [],
  jobs: [],
  clients: [],
  stages: [],
  candidate_statuses: [],
  owners: [],
  job_owners: [],
  candidate_employer: "",
  application_status: "",
};
export const submissionSearchSchema = z.object({
  candidate: z.uuid().optional().catch(undefined),
  q: z.string().max(200).optional().catch(undefined),
  filters: columnFiltersSchema.optional().catch(undefined),
  activity: z
    .partialRecord(
      z.enum(["contact", "cv", ...submissionActivityDateFields]),
      z
        .object({
          field: z.enum(submissionActivityDateFields),
          range: dateRange.optional(),
          missing: z.boolean().optional(),
        })
        .refine((value) => !(value.missing && (value.range?.from || value.range?.to))),
    )
    .optional()
    .catch(undefined),
  counts: z
    .object({
      interviewCount: z
        .string()
        .regex(/^\d{0,9}$/)
        .optional(),
      otherInterviewCount: z
        .string()
        .regex(/^\d{0,9}$/)
        .optional(),
    })
    .optional()
    .catch(undefined),
  sort: z
    .array(z.object({ id: z.enum(sortableSubmissionColumns), desc: z.boolean() }))
    .max(3)
    .optional()
    .catch(undefined),
  offset: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).optional().catch(undefined),
});
export type SubmissionSearch = z.infer<typeof submissionSearchSchema>;
